import {ICashierPaymentsApi} from '@wix/cashier-payments-widget';
import {usePanorama} from '@wix/yoshi-flow-editor';
import {toMonitorError} from '../../../../domain/utils/errorMonitor.utils';
import {
  isNoLongerChargeableErrorCode,
  isSuccessfulPaymentStatus,
} from '../../../../domain/utils/paymentRequest/paymentResult.utils';
import {
  ChargeOrderPaymentRequestResult,
  PaymentFlowMonitorBreadcrumbs,
} from '../../../../types/paymentRequestApp.types';

export type PayResult = {
  succeeded: boolean;
  navigatedAway?: boolean;
};

export const payWithChargeApi = async ({
  paymentsApi,
  chargeOrderPaymentRequest,
  refreshPaymentRequestState,
  navigateToPaymentErrorPage,
  navigateToExternalUrl,
  panorama,
}: {
  paymentsApi?: ICashierPaymentsApi;
  chargeOrderPaymentRequest: (paymentToken: string) => Promise<ChargeOrderPaymentRequestResult>;
  refreshPaymentRequestState: () => Promise<void>;
  navigateToPaymentErrorPage: () => void;
  navigateToExternalUrl: (url: string) => void;
  panorama: ReturnType<typeof usePanorama>;
}): Promise<PayResult> => {
  await paymentsApi?.prepareToPay?.();

  const initializeResponse = await paymentsApi?.initializePayment?.();
  const paymentToken = initializeResponse?.detailsId;

  if (!paymentToken) {
    throw new Error('Cashier initializePayment returned no payment token');
  }

  const chargeResult = await chargeOrderPaymentRequest(paymentToken);

  if (chargeResult.status === 'failed') {
    panorama.errorMonitor().reportError(...toMonitorError(chargeResult, PaymentFlowMonitorBreadcrumbs.error));

    if (isNoLongerChargeableErrorCode(chargeResult.code)) {
      void refreshPaymentRequestState();
    } else {
      navigateToPaymentErrorPage();
    }

    return {succeeded: false, navigatedAway: true};
  }

  const paymentResponseToken = chargeResult.paymentResponseToken;
  if (!paymentResponseToken) {
    return {succeeded: true};
  }

  try {
    const continuePaymentResponse = await paymentsApi?.continuePayment?.(paymentResponseToken);

    if (continuePaymentResponse?.redirectUrl) {
      navigateToExternalUrl(continuePaymentResponse.redirectUrl);
      return {succeeded: false, navigatedAway: true};
    }

    return {succeeded: isSuccessfulPaymentStatus(continuePaymentResponse?.clientStatus)};
  } catch (continuePaymentError) {
    panorama.errorMonitor().reportError(...toMonitorError(continuePaymentError, PaymentFlowMonitorBreadcrumbs.error));
    return {succeeded: false};
  }
};

export const pay = async ({
  useChargeApi,
  paymentsApi,
  chargeOrderPaymentRequest,
  paymentGatewayOrderId,
  refreshPaymentRequestState,
  navigateToPaymentErrorPage,
  navigateToExternalUrl,
  panorama,
}: {
  useChargeApi: boolean;
  paymentsApi?: ICashierPaymentsApi;
  chargeOrderPaymentRequest: (paymentToken: string) => Promise<ChargeOrderPaymentRequestResult>;
  paymentGatewayOrderId?: string;
  refreshPaymentRequestState: () => Promise<void>;
  navigateToPaymentErrorPage: () => void;
  navigateToExternalUrl: (url: string) => void;
  panorama: ReturnType<typeof usePanorama>;
}): Promise<PayResult> => {
  if (useChargeApi) {
    return payWithChargeApi({
      paymentsApi,
      chargeOrderPaymentRequest,
      refreshPaymentRequestState,
      navigateToPaymentErrorPage,
      navigateToExternalUrl,
      panorama,
    });
  }

  await paymentsApi?.prepareToPay?.();
  const paymentResult = await paymentsApi?.pay?.({orderId: paymentGatewayOrderId});
  return {succeeded: isSuccessfulPaymentStatus(paymentResult?.clientStatus)};
};
